import { observable } from '@legendapp/state';
import { logger } from '@/lib/utils/logger';
import { FormAnswer } from '@/types';

// Types
export interface FormState {
  questionnaire: {
    submitted: boolean;
    formData: FormAnswer | null;
    answerId: string | null;
    isValid: boolean;
    errors: Record<string, string>;
    isDirty: boolean;
  };
  modal: {
    isOpen: boolean;
    type: 'questionnaire' | 'feedback' | null;
    data?: Record<string, any>;
  };
}

// Initial state
const initialState: FormState = {
  questionnaire: {
    submitted: false,
    formData: null,
    answerId: null,
    isValid: false,
    errors: {},
    isDirty: false,
  },
  modal: {
    isOpen: false,
    type: null,
  },
};

// Create the observable store
export const formState = observable<FormState>(initialState);

// Action creators
export const formActions = {
  // Questionnaire actions
  setFormData: (data: Partial<FormAnswer>) => {
    logger.debug('[formStore] Setting form data', { data });
    const currentState = formState.get();
    formState.set({
      ...currentState,
      questionnaire: {
        ...currentState.questionnaire,
        formData: {
          ...currentState.questionnaire.formData,
          ...data,
        } as FormAnswer,
        isDirty: true,
      },
    });
  },

  setAnswerId: (answerId: string | null) => {
    logger.debug('[formStore] Setting answer ID', { answerId });
    const currentState = formState.get();
    formState.set({
      ...currentState,
      questionnaire: {
        ...currentState.questionnaire,
        answerId,
      },
    });
  },

  setSubmitted: (submitted: boolean) => {
    logger.debug('[formStore] Setting submitted state', { submitted });
    const currentState = formState.get();
    formState.set({
      ...currentState,
      questionnaire: {
        ...currentState.questionnaire,
        submitted,
      },
    });
  },

  setErrors: (errors: Record<string, string>) => {
    logger.debug('[formStore] Setting form errors', { errors });
    const currentState = formState.get();
    formState.set({
      ...currentState,
      questionnaire: {
        ...currentState.questionnaire,
        errors,
        isValid: Object.keys(errors).length === 0,
      },
    });
  },

  resetForm: () => {
    logger.debug('[formStore] Resetting form state');
    const currentState = formState.get();
    formState.set({
      ...currentState,
      questionnaire: {
        ...initialState.questionnaire,
      },
    });
  },

  // Modal actions
  openModal: (type: FormState['modal']['type'], data?: Record<string, any>) => {
    logger.debug('[formStore] Opening modal', { type, data });
    const currentState = formState.get();
    formState.set({
      ...currentState,
      modal: {
        isOpen: true,
        type,
        data,
      },
    });
  },

  closeModal: () => {
    logger.debug('[formStore] Closing modal');
    const currentState = formState.get();
    formState.set({
      ...currentState,
      modal: {
        ...initialState.modal,
      },
    });
  },

  reset: () => {
    logger.debug('[formStore] Resetting all state');
    formState.set(initialState);
  },
};

// Selectors
export const formSelectors = {
  // Questionnaire selectors
  getFormData: () => formState.questionnaire.formData.get(),
  getAnswerId: () => formState.questionnaire.answerId.get(),
  isSubmitted: () => formState.questionnaire.submitted.get(),
  isValid: () => formState.questionnaire.isValid.get(),
  getErrors: () => formState.questionnaire.errors.get(),
  isDirty: () => formState.questionnaire.isDirty.get(),

  // Modal selectors
  isModalOpen: () => formState.modal.isOpen.get(),
  getModalType: () => formState.modal.type.get(),
  getModalData: () => formState.modal.data.get(),
};
